import React, { startTransition, useEffect, useState } from 'react';
import Button from './Button';
import { Link, useNavigate, type NavigateOptions } from 'react-router-dom';
import { PortalButtonGlimmer } from '../components/PortalGlimmerElements';
import { PageHeaderGlimmer } from '../tenant_portal/shared/PageHeader';
import { OauthProvider } from '../components/Constants';
import Alert from '../components/Alert';
import { formatProviderName, getProviderLogoPath } from '../components/Tools';
import loginStyles from '../../../stylesheets/components/login.module.scss';
import {
  getPreloadedErrorMessages,
  getProviderFromMessage,
  isAccountExistsMessage,
  isConnectivityMessage,
  isProviderLinkedOauthAccountMessage,
} from './ErrorTools';
import SomethingWentWrongAlert from './SomethingWentWrongAlert';
import { buildOauthRedirectUrl } from './RouteIntents';

interface OptionsProps {
  emailPath: string;
  emailState?: NavigateOptions['state'];
  emailQueryParams?: Record<string, string>;
  activationKey?: string;
  createAccount?: boolean;
  encryptedEmail?: string;
  existingAccountActionTo?: string;
}

type LoginOptionInlineError = {
  provider: OauthProvider;
  text: string;
};

type OptionsAlertData = {
  actionHref?: string;
  actionLabel?: string;
  actionTo?: string;
  dismissible?: boolean;
  body: string;
  title: string;
};

let unconsumedPreloadedErrors: typeof preloadedErrors = preloadedErrors;

const getOptionInlineError = (
  provider: OauthProvider | undefined,
  message: string
): LoginOptionInlineError | null => {
  if (!provider || !isConnectivityMessage(message)) return null;

  if (message.includes('check your connection')) {
    return {
      provider,
      text: 'Check your connection and try again.',
    };
  }

  return {
    provider,
    text: `${formatProviderName(
      provider
    )} is having trouble right now. Try again in a minute or use email.`,
  };
};

const getOptionAlertState = ({
  errors,
  existingAccountActionTo,
}: {
  errors: typeof preloadedErrors;
  existingAccountActionTo?: string;
}) => {
  const messages = getPreloadedErrorMessages(errors);
  if (messages.length === 0) {
    return {
      alert: null as OptionsAlertData | null,
      optionInlineError: null as LoginOptionInlineError | null,
    };
  }

  const combinedMessage = messages.join(' ');
  const normalizedMessage = combinedMessage.toLowerCase();
  const provider = getProviderFromMessage(normalizedMessage);

  if (
    !isProviderLinkedOauthAccountMessage(normalizedMessage) &&
    isAccountExistsMessage(normalizedMessage)
  ) {
    return {
      alert: {
        title: 'Account already exists',
        body: 'That email is already registered. Sign in instead.',
        actionLabel: 'Sign in',
        actionTo:
          existingAccountActionTo || window.RAILS_CONSTANTS['login_path'],
      },
      optionInlineError: null,
    };
  }

  if (isConnectivityMessage(normalizedMessage)) {
    return {
      alert: {
        title: "Can't connect",
        body: 'Try a different method or come back shortly.',
      },
      optionInlineError: getOptionInlineError(provider, normalizedMessage),
    };
  }

  return {
    alert: {
      title: 'Something went wrong',
      body: combinedMessage.trim(),
    },
    optionInlineError: null,
  };
};

const Options = ({
  emailPath,
  emailState,
  emailQueryParams,
  activationKey,
  createAccount = false,
  encryptedEmail,
  existingAccountActionTo,
}: OptionsProps) => {
  const navigate = useNavigate();
  const [flashErrors, setFlashErrors] = useState<typeof preloadedErrors>(
    () => unconsumedPreloadedErrors
  );
  useEffect(() => {
    unconsumedPreloadedErrors = null;
  }, []);
  const { alert, optionInlineError } = getOptionAlertState({
    errors: flashErrors,
    existingAccountActionTo,
  });
  const clearFlashErrors = () => setFlashErrors(null);
  const genericAlert =
    alert?.title === 'Something went wrong' ? (
      <SomethingWentWrongAlert
        body={alert.body}
        onDismiss={clearFlashErrors}
        resetKey={flashErrors}
      />
    ) : null;
  const visibleAlert = alert;

  const getOptionHelperId = (provider: OauthProvider) => {
    return optionInlineError?.provider === provider
      ? `login-option-helper-${provider}`
      : undefined;
  };

  const getOptionHelperText = (provider: OauthProvider) => {
    return optionInlineError?.provider === provider
      ? optionInlineError.text
      : undefined;
  };

  const emailRedirectUrl = () => {
    const params = new URLSearchParams(emailQueryParams ?? {});
    const queryString = params.toString();
    return queryString !== '' ? `${emailPath}?${queryString}` : emailPath;
  };

  const oauthRedirectUrl = (provider: OauthProvider) => {
    return buildOauthRedirectUrl({
      pathname: window.location.pathname,
      provider,
      activationKey,
      createAccount,
      encryptedEmail,
    });
  };

  const handleLoginButton = (provider: OauthProvider) => {
    if (provider === OauthProvider.EMAIL) {
      startTransition(() => {
        navigate(
          emailRedirectUrl(),
          emailState ? { state: emailState } : undefined
        );
      });
      return;
    }

    window.location.href = oauthRedirectUrl(provider);
  };

  return (
    <>
      {genericAlert}
      {visibleAlert && visibleAlert.title !== 'Something went wrong' && (
        <Alert
          dataTestClass="login-error-banner"
          onClose={
            visibleAlert.dismissible === false ? undefined : clearFlashErrors
          }
          severity="error"
          title={
            <span data-test-class="login-error-banner-title">
              {visibleAlert.title}
            </span>
          }
        >
          <span data-test-class="login-error-banner-body">
            {visibleAlert.body}
          </span>
          {visibleAlert.actionTo && visibleAlert.actionLabel && (
            <>
              {' '}
              <Link
                className={loginStyles.loginAlertLink}
                to={visibleAlert.actionTo}
              >
                {visibleAlert.actionLabel}
              </Link>
            </>
          )}
          {visibleAlert.actionHref && visibleAlert.actionLabel && (
            <>
              {' '}
              <a
                className={loginStyles.loginAlertLink}
                href={visibleAlert.actionHref}
              >
                {visibleAlert.actionLabel}
              </a>
            </>
          )}
        </Alert>
      )}
      <div className={loginStyles.loginSocialButtons}>
        <div className={loginStyles.loginSocialGroup}>
          <Button
            onClick={() => handleLoginButton(OauthProvider.GOOGLE)}
            logoPath={getProviderLogoPath(OauthProvider.GOOGLE)}
            className={loginStyles.loginSocialButton}
            helperId={getOptionHelperId(OauthProvider.GOOGLE)}
            helperText={getOptionHelperText(OauthProvider.GOOGLE)}
            helperTestClass="login-option-error-helper"
            invalid={optionInlineError?.provider === OauthProvider.GOOGLE}
            logoClassName={loginStyles.loginButtonLogo}
          >
            Continue with Google
          </Button>
          <Button
            onClick={() => handleLoginButton(OauthProvider.APPLE)}
            logoPath={getProviderLogoPath(OauthProvider.APPLE)}
            className={loginStyles.loginSocialButton}
            helperId={getOptionHelperId(OauthProvider.APPLE)}
            helperText={getOptionHelperText(OauthProvider.APPLE)}
            helperTestClass="login-option-error-helper"
            invalid={optionInlineError?.provider === OauthProvider.APPLE}
            logoClassName={loginStyles.loginButtonLogo}
          >
            Continue with Apple
          </Button>
          <Button
            onClick={() => handleLoginButton(OauthProvider.FACEBOOK)}
            logoPath={getProviderLogoPath(OauthProvider.FACEBOOK)}
            className={loginStyles.loginSocialButton}
            helperId={getOptionHelperId(OauthProvider.FACEBOOK)}
            helperText={getOptionHelperText(OauthProvider.FACEBOOK)}
            helperTestClass="login-option-error-helper"
            invalid={optionInlineError?.provider === OauthProvider.FACEBOOK}
            logoClassName={loginStyles.loginButtonLogo}
          >
            Continue with Facebook
          </Button>
          <Button
            onClick={() => handleLoginButton(OauthProvider.MICROSOFT)}
            logoPath={getProviderLogoPath(OauthProvider.MICROSOFT)}
            className={loginStyles.loginSocialButton}
            helperId={getOptionHelperId(OauthProvider.MICROSOFT)}
            helperText={getOptionHelperText(OauthProvider.MICROSOFT)}
            helperTestClass="login-option-error-helper"
            invalid={optionInlineError?.provider === OauthProvider.MICROSOFT}
            logoClassName={loginStyles.loginButtonLogo}
          >
            Continue with Microsoft
          </Button>
        </div>
        <div className={loginStyles.loginDivider}>
          <span className={loginStyles.loginDividerLine} />
          <span className={loginStyles.loginDividerLabel}>or</span>
          <span className={loginStyles.loginDividerLine} />
        </div>
        <Button
          onClick={() => handleLoginButton(OauthProvider.EMAIL)}
          logoPath={getProviderLogoPath(OauthProvider.EMAIL)}
          id="email-login-button"
          className={loginStyles.loginSocialButton}
          helperId={getOptionHelperId(OauthProvider.EMAIL)}
          helperText={getOptionHelperText(OauthProvider.EMAIL)}
          helperTestClass="login-option-error-helper"
          invalid={optionInlineError?.provider === OauthProvider.EMAIL}
          logoClassName={loginStyles.loginButtonLogo}
        >
          Continue with Email
        </Button>
      </div>
    </>
  );
};

export const OptionsGlimmer = () => {
  return (
    <>
      <PageHeaderGlimmer />
      <PortalButtonGlimmer />
      <PortalButtonGlimmer />
      <PortalButtonGlimmer />
      <PortalButtonGlimmer />
      <PortalButtonGlimmer />
    </>
  );
};

export default Options;
